import React, { useState, useEffect } from "react";
import { Grid as AntGrid, ConfigProvider } from 'antd';
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { Provider as ReduxProvider } from "react-redux";
import {
  LaptopOutlined,
  NotificationOutlined,
  UserOutlined,
  AppstoreOutlined,
  MailOutlined,
  SettingOutlined,
  setTwoToneColor,
} from "@ant-design/icons";
import {
  Text, useTheme, View, Image, Heading,
  Flex,
  Grid,
 } from '@aws-amplify/ui-react';
import type { MenuProps } from "antd";
import {
  Breadcrumb,
  Layout,
  Menu,
  // theme,
  Button,
  Space,
  Checkbox,
  Form,
  Input,
} from "antd";
import { Auth } from "aws-amplify";
import { Authenticator, ThemeProvider as AmplifyThemeProvider } from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";
import { components } from "./components/login/loginComp";
import { setupAmplify } from "./aws-exports";

import NavBar from "./components/navbar";
import SideBar from "./components/sidebar";
import TestApi from "./components/testapi";
import Home from "./components/home";
import HomeAdmin from "./components/home-admin";
import JYSubmit from "./components/jy-submit";
import JYHistory from "./components/jy-history";
import RDHistory from "./components/rd-history";
import UMapping from "./components/u-mapping";
import JYSubmitTest from "./components/test/jy-submit-test";
import JYSubmitTestNew from "./components/test/jy-submit-test-new";
import Notif from "./components/testNotif";
import ManualCallback from "./components/manual-callback";
import QueryRedemption from "./components/queryRedemption";
import SBSubmit from "./components/slowboat/submit";
import SBMapping from "./components/slowboat/mapping";
import SBInventory from "./components/slowboat/inventory";
import EMNetworkSelection from "./components/elitemobile/recharge/NetworkSelection";
import EMPhoneNumberInput from './components/elitemobile/recharge/PhoneNumberInput';
import EMTopupSelection from './components/elitemobile/recharge/TopUpSelection';
import EMConfirmation from './components/elitemobile/recharge/Confirmation';
import ShopeeViewOrders from "./components/shopee-view-orders";
import RechargeHistory from "./components/elitemobile/recharge/RechargeHistory";
import SBRedemptions from "./components/slowboat/redemption";
import theme from "./theme";
import amplifyTheme from "./themeAmplify";
import Announcements from "./components/announcements";
import AnnouncementBanner from "./components/announcements/components/AnnouncementBanner";
import { store } from "./config/store";
import VNMapping from "./components/mobifone/mapping";
import VNInventory from "./components/mobifone/inventory";
import VNRedemptions from "./components/mobifone/redemption";
import VNSubmit from "./components/mobifone/submit";
import ManageMappingPage from "./components/datamapping/manage";
import JoyTgtMappingPage from "./components/datamapping/joy-tgt";
import UploadMappingPage from "./components/datamapping/upload";
import ProductGroupsPage from "./components/product-groups";
import InventoryUploadPage from "./components/inventory/upload";
import ManageInventoryPage from "./components/inventory/manage";
import ManageRedemptionPage from "./components/redemption/manage";
import RedemptionManualSubmit from "./components/redemption/manual";
import ProductGroupUploadPage from "./components/product-groups/upload/index";

// Physical Sim Routes
import PSIMOrdersNewPage from "./components/psim/orders/new";
import PSIMOrdersManagePage from "./components/psim/orders/manage";
import PSIMInventoryManagePage from "./components/psim/inventory/manage";
import PSIMInventoryUploadPage from "./components/psim/uploads/inventory";
import PSIMActivationPendingPage from "./components/psim/activation/pending";
import PSIMActivationReportPage from "./components/psim/activation/report";
import PSIMUploadProductPlansPage from "./components/psim/uploads/product-plans";
import PSIMUploadProductTypesPage from "./components/psim/uploads/product-types";
import PSIMUploadInventoryPage from "./components/psim/uploads/inventory";
import DeprecatePage from "./components/inventory/deprecate/page";
import USIMMappingManagePage from "./components/psim/mappings/manage";
import USIMMappingUploadPage from "./components/psim/mappings/upload";
import UsimInventoryReportPage from "./components/psim/inventory/report";
import WebshopPromotions from "./components/webshop/promotions";
import WebshopBundles from "./components/webshop/bundleDeals";
import UsimSkuPage from "./components/psim/uploads/product-sku";
import DealRedemptionPage from "./components/webshop/redemptions";
import RzpViewOrders from "./components/webshop/rzp";
import AccountManagement from "./components/user-settings/AccountManagement";
import DistributorsPage from "./components/distributors";
import DistributorInventoryPage from "./components/distributors/inventory";
import SimplyWebUserManagement from "./components/webshop/users";
import PsimOrderSubmissionPage from "./components/psim/orders/new/RetailOrderPage"
import WebshopProductsPage from "./components/webshop/wsProducts/index"
import WebshopUploadsPage from "./components/webshop/wsProducts/UploadHistoryPage"
import WsAssetMgmtPage from "./components/webshop/assetMgmt/index"
import ExportOrdersPage from "./components/platforms/export-orders";
import RevenueDashboardPage from "./components/sales/revenue";
import SimplyWebRevenueDashboardPage from "./components/sales/simplyWebRevenue";
import RevenueSettingsPage from "./components/sales/revenueSettings";
import SlowboatPassportsPage from "./components/slowboat/passports";
// import PSIMProductPlansUploadPage from "./components/psim/product/plans";


//
// Menu Props for ant.d
//
const { useBreakpoint } = AntGrid;
const { Header, Content, Footer, Sider } = Layout;

//
// Amplify init
//
let credentials = null;
await setupAmplify().then((creds) => {
  console.log(`Amplify configured with ${JSON.stringify(creds)}`);
  credentials = creds;
});
Auth.configure(credentials);

//
// Theme configuration
//
if (theme?.token?.colorPrimary !== undefined) {
  setTwoToneColor(theme.token.colorPrimary)
}

//
// Fn Component
//
const App: React.FC = () => {

  const [session, setSession] = useState<any>(null);
  const screens = useBreakpoint();


  const { tokens } = useTheme();

  return (
    <>
      <ReduxProvider store={store}>
        <ConfigProvider theme={theme}>
          <AmplifyThemeProvider theme={amplifyTheme}>
          <Authenticator
            loginMechanisms={["username"]}
            signUpAttributes={["email"]}
            components={components}
            hideSignUp
          >
          <Router>
          <Layout>
            <Header
              style={{
                position: "sticky",
                top: 0,
                zIndex: 100,
                width: "100%",
                display: "flex",
                alignItems: "center",
                textAlign:"right"
              }}
            >
              <NavBar />
            </Header>
            <Layout>
            <Sider
                    width={200}
                    breakpoint="md" // Responsive collapse behavior
                    collapsedWidth={screens.md ? "80" : "80"} // set collapsed width to 0 on small screens
                    collapsible
                    theme="light"
                  >
                    <SideBar />
                </Sider>
              <Layout style={{ padding: "24px 24px 24px", minHeight: "95vh" }}>
                <Content
                  style={{
                    padding: 24,
                    margin: 0,
                    minHeight: 280,
                  }}
                >
                  <AnnouncementBanner />
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/announcements" element={<Announcements />} />
                    {/* <Route path="/home-admin" element={<HomeAdmin />} /> */}
                    <Route path="/home-admin" element={<Home />} />
                    <Route path="/testapi" element={<TestApi />} />
                    <Route path="/jy-submit" element={<JYSubmit />} />
                    <Route path="/jy-submit-test" element={<JYSubmitTest />} />
                    <Route path="/jy-history" element={<JYHistory />} />
                    <Route path="/rd-history" element={<RDHistory />} />
                    <Route path="/u-mapping" element={<UMapping/>} />
                    <Route path="/test-notif" element={<Notif/>} />
                    <Route path="/jy-submit-test-new" element={<JYSubmitTestNew/>} />
                    <Route path="/sb-submit" element={<SBSubmit />} />
                    <Route path="/sb-mapping" element={<SBMapping />} />
                    <Route path="/sb-inventory" element={<SBInventory />} />
                    <Route path="/sb-redemptions" element={<SBRedemptions />} />
                    <Route path="/vn-submit" element={<VNSubmit />} />
                    <Route path="/vn-mapping" element={<VNMapping />} />
                    <Route path="/vn-inventory" element={<VNInventory />} />
                    <Route path="/vn-redemptions" element={<VNRedemptions />} />
                    <Route path="/manual-cb" element={<ManualCallback/>} />
                    <Route path="/query-rdp" element={<QueryRedemption/>} />
                    <Route path="/em-recharge" element={<EMNetworkSelection />} />
                    <Route path="/phone-number" element={<EMPhoneNumberInput />} />
                    <Route path="/topup" element={<EMTopupSelection />} />
                    <Route path="/confirmation" element={<EMConfirmation/>} />
                    <Route path="/shopee-view-orders" element={<ShopeeViewOrders />} />
                    <Route path="/platforms/export-orders" element={<ExportOrdersPage />} />
                    <Route path="/sales/revenue" element={<RevenueDashboardPage />} />
                    <Route path="/sales/revenue/simply-web" element={<SimplyWebRevenueDashboardPage />} />
                    <Route path="/sales/revenue/settings" element={<RevenueSettingsPage />} />
                    <Route path="/em-recharge-history" element={<RechargeHistory />} />
                    <Route path="/datamapping" element={<ManageMappingPage />} />
                    <Route
                      path="/datamapping/joy-tgt"
                      element={<JoyTgtMappingPage />}
                    />
                    <Route path="/datamapping/upload" element={<UploadMappingPage />} />
                    <Route path="/product-groups/" element={<ProductGroupsPage />} />
                    <Route path="/product-groups/upload" element={<ProductGroupUploadPage />} />
                    <Route path="/webshop/promotions" element={<WebshopPromotions />} />
                    <Route path="/webshop/bundles" element={<WebshopBundles />} />
                    <Route path="/webshop/redemptions" element={<DealRedemptionPage />} />
                    <Route path="/webshop/orders" element={<RzpViewOrders />} />
                    <Route path="/webshop/users" element={<SimplyWebUserManagement />} />
                    <Route path="/user-settings/accounts" element={<AccountManagement />} />
                    <Route path="/inventory" element={<ManageInventoryPage />} />
                    <Route path="/inventory/upload" element={<InventoryUploadPage />} />
                    <Route path="/inventory/deprecate" element={<DeprecatePage />} />
                    <Route path="/redemption" element={<ManageRedemptionPage />} />
                    <Route path="/redemption/manual" element={<RedemptionManualSubmit />} />
                    <Route path="/slowboat/passports" element={<SlowboatPassportsPage />} />
                    <Route path="/psim/orders/new" element={<PSIMOrdersNewPage />} />
                    <Route path="/usim/orders/new" element={<PsimOrderSubmissionPage />} />
                    <Route path="/psim/orders/manage" element={<PSIMOrdersManagePage />} />
                    <Route path="/psim/inventory" element={<PSIMInventoryManagePage />} />
                    <Route path="/psim/inventory/upload" element={<PSIMInventoryUploadPage />} />
                    <Route path="/psim/inventory/report" element={<UsimInventoryReportPage />} />
                    <Route path="/psim/activation/pending" element={<PSIMActivationPendingPage />} />
                    <Route path="/psim/activation/report" element={<PSIMActivationReportPage />} />
                    <Route path="/psim/uploads/product-types" element={<PSIMUploadProductTypesPage />} />
                    <Route path="/psim/uploads/inventory" element={<PSIMUploadInventoryPage />} />
                    <Route path="/psim/uploads/product-sku" element={<PSIMUploadProductPlansPage />} />
                    {/* <Route path="/psim/product/plans" element={<PSIMProductPlansUploadPage />} /> */}
                    <Route path="/psim/uploads/product-plans" element={<UsimSkuPage />} />
                    <Route path="/psim/mappings/" element={<USIMMappingManagePage />} />
                    <Route path="/psim/mappings/upload" element={<USIMMappingUploadPage />} />
                    <Route path="/webshop/products" element={<WebshopProductsPage />} />
                    <Route path="/webshop/products/upload" element={<WebshopUploadsPage />} />
                    <Route path= "/webshop/assets" element={<WsAssetMgmtPage/>} />
                    <Route path="/distributor-portal" element={<DistributorsPage />} />
                    <Route path="/distributor-portal/inventory" element={<DistributorInventoryPage />} />
                  </Routes>
                </Content>
                <Footer style={{ textAlign: "center" }}>
                  Echo Your Travels Admin Portal
                </Footer>
              </Layout>
            </Layout>
          </Layout>
        </Router>
        </Authenticator>
        </AmplifyThemeProvider>
        </ConfigProvider>
      </ReduxProvider>
    </>
  );
};

export default App;
